import type Game from '../../Game';

const METRICS_UPLOAD_ENDPOINT = 'https://prod.persuade-creative.hytopia.com/Play/Metrics/UploadClientMetrics';
const METRICS_SAMPLE_WINDOW_MS = 30_000;
const METRICS_SAMPLE_INTERVAL_MS = 1_000;

type MetricStats = {
  min: number;
  max: number;
  total: number;
  count: number;
};

type UploadMetricStats = {
  min: number;
  avg: number;
  max: number;
};

export default class ClientMetricsUploader {
  private _game: Game;
  private _running: boolean = false;
  private _lobbyId: string | null;
  private _sessionToken: string | null;
  private _sessionTimeSeconds: number = 0;
  private _playTimeSeconds: number = 0;

  public constructor(game: Game) {
    this._game = game;

    const params = new URLSearchParams(window.location.search);
    this._lobbyId = params.get('lobbyId');
    this._sessionToken = params.get('sessionToken');
  }

  public start(): void {
    if (this._running) {
      return;
    }

    if (!this._lobbyId || !this._sessionToken) {
      console.warn('ClientMetricsUploader.start(): Missing lobbyId/sessionToken, metrics upload disabled.');
      return;
    }

    this._running = true;
    void this._runUploadLoop();
  }

  public stop(): void {
    this._running = false;
  }

  private async _runUploadLoop(): Promise<void> {
    while (this._running) {
      let fpsStats = this._resetMetricStats();
      let latencyStats = this._resetMetricStats();
      const windowStartMs = performance.now();
      let previousSampleMs = windowStartMs;
      let windowPlayTimeMs = 0;

      while (this._running && (performance.now() - windowStartMs) < METRICS_SAMPLE_WINDOW_MS) {
        await this._sleep(METRICS_SAMPLE_INTERVAL_MS);

        const nowMs = performance.now();
        const elapsedSampleMs = nowMs - previousSampleMs;
        previousSampleMs = nowMs;

        if (document.visibilityState === 'visible') {
          windowPlayTimeMs += elapsedSampleMs;
        }

        const fps = this._game.performanceMetricsManager.fps;
        if (Number.isFinite(fps) && fps > 0) {
          fpsStats = this._recordMetricValue(fpsStats, fps);
        }

        const latencyMs = this._game.networkManager.roundTripTimeS * 1000;
        if (Number.isFinite(latencyMs) && latencyMs > 0) {
          latencyStats = this._recordMetricValue(latencyStats, latencyMs);
        }
      }

      if (!this._running) {
        return;
      }

      const elapsedWindowMs = performance.now() - windowStartMs;

      // Session time always accumulates elapsed wall time.
      this._sessionTimeSeconds += elapsedWindowMs / 1000;
      this._playTimeSeconds += windowPlayTimeMs / 1000;

      await this._uploadMetrics(this._toUploadMetricStats(fpsStats), this._toUploadMetricStats(latencyStats));
    }
  }

  private async _uploadMetrics(fps: UploadMetricStats, latency: UploadMetricStats): Promise<void> {
    try {
      const response = await fetch(METRICS_UPLOAD_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lobbyId: this._lobbyId,
          sessionToken: this._sessionToken,
          fps,
          latency,
          sessionTimeSeconds: Math.round(this._sessionTimeSeconds),
          playTimeSeconds: Math.round(this._playTimeSeconds),
        }),
      });

      if (!response.ok) {
        console.warn(`ClientMetricsUploader._uploadMetrics(): Upload failed with status ${response.status}`);
      }
    } catch (error) {
      console.warn('ClientMetricsUploader._uploadMetrics(): Upload error', error);
    }
  }

  private _resetMetricStats(): MetricStats {
    return {
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      total: 0,
      count: 0,
    };
  }

  private _recordMetricValue(stats: MetricStats, value: number): MetricStats {
    return {
      min: Math.min(stats.min, value),
      max: Math.max(stats.max, value),
      total: stats.total + value,
      count: stats.count + 1,
    };
  }

  private _toUploadMetricStats(stats: MetricStats): UploadMetricStats {
    if (stats.count === 0) {
      return { min: 0, avg: 0, max: 0 };
    }

    return {
      min: Math.round(stats.min),
      avg: Math.round(stats.total / stats.count),
      max: Math.round(stats.max),
    };
  }

  private _sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
